import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// ── Button ─────────────────────────────────────────────────────────────────
const btnStyles = {
  base: {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    padding: '9px 18px', borderRadius: 'var(--radius-md)',
    fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.01em', lineHeight: 1.3,
    transition: 'all var(--transition)', cursor: 'pointer', border: 'none',
  },
  primary: { background: 'var(--ink)', color: '#fff' },
  accent: { background: 'var(--ink)', color: '#fff' },
  teal: { background: 'var(--ink)', color: '#fff' },
  outline: { background: 'var(--canvas-white)', color: 'var(--ink)', border: '1px solid var(--line)' },
  ghost: { background: 'transparent', color: 'var(--ink-soft)' },
  danger: { background: 'var(--danger)', color: '#fff' },
  // Text-weight action (tertiary) — looks like a link, behaves like a button.
  link: { background: 'none', color: 'var(--ink)', padding: 0, textDecoration: 'underline', textUnderlineOffset: 2, fontWeight: 400 },
  sm: { padding: '6px 13px', fontSize: '0.8rem' },
  lg: { padding: '14px 28px', fontSize: '1rem' },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
};

export function Btn({ variant = 'primary', size, loading, disabled, children, style, ...props }) {
  const s = {
    ...btnStyles.base,
    ...btnStyles[variant],
    ...(size && btnStyles[size]),
    ...(variant === 'link' ? { padding: 0 } : {}),
    ...(disabled || loading ? btnStyles.disabled : {}),
    ...style,
  };
  return (
    <button style={s} disabled={disabled || loading} {...props}>
      {loading && <Spinner size={14} color="currentColor" />}
      {children}
    </button>
  );
}

// ── Spinner ────────────────────────────────────────────────────────────────
export function Spinner({ size = 20, color = 'var(--accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────
export function Card({ children, style, onClick, hover }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        background: 'var(--canvas-white)', borderRadius: 'var(--radius-panel)',
        boxShadow: hov && hover ? 'var(--shadow-md)' : 'none',
        border: 'var(--panel-border)',
        transition: 'all var(--transition)',
        transform: hov && hover ? 'translateY(-1px)' : 'none',
        cursor: onClick || hover ? 'pointer' : 'default',
        ...style
      }}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {children}
    </div>
  );
}

// ── Alert ──────────────────────────────────────────────────────────────────
const alertColors = {
  error: { bg: 'var(--danger-pale)', border: '#e8b4b0', text: 'var(--danger)' },
  success: { bg: 'var(--success-pale)', border: '#a3d9bb', text: 'var(--teal)' },
  info: { bg: 'var(--teal-pale)', border: '#a3d4cf', text: 'var(--teal)' },
  warn: { bg: 'var(--warn-pale)', border: '#f0c490', text: 'var(--warn)' },
};

export function Alert({ type = 'info', children }) {
  const c = alertColors[type];
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 'var(--radius-md)',
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.text, fontSize: '0.88rem', lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

// ── FormField ──────────────────────────────────────────────────────────────
export function FormField({ label, required, error, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      {label && (
        <label style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--body-ink)' }}>
          {label} {required && <span style={{ color: 'var(--accent)' }}>*</span>}
        </label>
      )}
      {children}
      {hint && <span style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{hint}</span>}
      {error && <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{error}</span>}
    </div>
  );
}

const inputBase = {
  padding: '8px 11px', borderRadius: 'var(--radius-md)',
  border: '1px solid var(--line)',
  background: 'var(--canvas-white)', color: 'var(--ink)',
  fontSize: '0.9rem', outline: 'none', width: '100%',
  transition: 'border-color var(--transition)',
};

export function Input({ error, style, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      style={{ ...inputBase, borderColor: error ? 'var(--danger)' : focus ? 'var(--ink-mid)' : 'var(--line)', ...style }}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      {...props}
    />
  );
}

export function Select({ error, style, children, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <select
      style={{ ...inputBase, borderColor: error ? 'var(--danger)' : focus ? 'var(--ink-mid)' : 'var(--line)', cursor: 'pointer', ...style }}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      {...props}
    >
      {children}
    </select>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────
const badgeColors = {
  pending: { bg: 'var(--warn-pale)', color: 'var(--warn)' },
  active: { bg: 'var(--teal-pale)', color: 'var(--teal)' },
  completed: { bg: 'var(--success-pale)', color: 'var(--success)' },
  cancelled: { bg: '#f0f0f0', color: 'var(--ink-soft)' },
  default: { bg: 'var(--canvas-warm)', color: 'var(--ink-soft)' },
};

export function Badge({ status, children }) {
  const c = badgeColors[status] || badgeColors.default;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: '999px',
      fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap',
      background: c.bg, color: c.color,
    }}>
      {children}
    </span>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 520, footer }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(4px)', zIndex: 1000, padding: '24px',
    }} onClick={onClose}>
      <div className="modal-box" style={{
        background: 'var(--canvas-white)', borderRadius: 'var(--radius-panel)',
        boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width,
        maxHeight: '90vh', overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 28px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem' }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ color: 'var(--ink-soft)', fontSize: '1.4rem', cursor: 'pointer', background: 'none', border: 'none', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '6px 28px 24px' }}>{children}</div>
        {footer && (
          <div style={{ position: 'sticky', bottom: 0, background: 'var(--canvas-white)', borderTop: '1px solid var(--line)', padding: '12px 28px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ConfirmDialog ──────────────────────────────────────────────────────────
// Site-styled replacement for window.confirm: Modal + Cancel / confirm in the footer.
// Error (if any) stays inside the dialog so the user can retry or cancel.
export function ConfirmDialog({ open, title, children, confirmLabel = 'Confirm', danger, busy, error, onConfirm, onCancel }) {
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onCancel}
      title={title}
      width={440}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <Btn variant="outline" size="sm" onClick={onCancel} disabled={busy}>Cancel</Btn>
          <Btn variant={danger ? 'danger' : 'primary'} size="sm" onClick={onConfirm} loading={busy}>{confirmLabel}</Btn>
        </div>
      }
    >
      <div style={{ fontSize: '0.9rem', color: 'var(--ink-soft)', lineHeight: 1.55 }}>{children}</div>
      {error && <div style={{ marginTop: 14 }}><Alert type="error">{error}</Alert></div>}
    </Modal>
  );
}

// ── PageHeader ─────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, gap: '16px', flexWrap: 'wrap', background: 'var(--canvas-white)', border: 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: '24px 30px' }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.9rem', color: 'var(--ink)', lineHeight: 1.2 }}>{title}</h1>
        {subtitle && <p style={{ color: 'var(--body-ink)', marginTop: 6, fontSize: '1rem', maxWidth: 760 }}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── Breadcrumb ─────────────────────────────────────────────────────────────
// "Where am I" line above a page title: [{ label, to }] — the last item is the current page.
export function Breadcrumb({ items }) {
  return (
    <nav aria-label="Breadcrumb" style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden>›</span>}
          {it.to && i < items.length - 1
            ? <Link to={it.to} style={{ color: 'var(--ink-soft)', textDecoration: 'none' }}>{it.label}</Link>
            : <span style={{ color: i === items.length - 1 ? 'var(--ink-soft)' : undefined }}>{it.label}</span>}
        </React.Fragment>
      ))}
    </nav>
  );
}

// ── SectionLabel ───────────────────────────────────────────────────────────
// Small uppercase label above a group of fields / a list inside a card.
export const sectionLabelStyle = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 };
export function SectionLabel({ children, style }) {
  return <div style={{ ...sectionLabelStyle, ...style }}>{children}</div>;
}

// ── SectionCard ────────────────────────────────────────────────────────────
// One block of a page: title (+ action on the right), one hint line, content.
// tone: 'gate' (a decision point) or 'done' (a confirmed gate) draws a left rule.
export function SectionCard({ title, hint, action, tone, children, style }) {
  const rule = tone === 'gate' ? 'var(--ink)' : tone === 'done' ? 'var(--success)' : null;
  // contract .sec: 14px panel, title as a muted caps label, body text in --body-ink
  const hasBody = React.Children.toArray(children).length > 0;
  return (
    <Card style={{ padding: '22px 28px', marginBottom: 14, ...(tone === 'gate' ? { border: '2px solid var(--gate)' } : rule ? { borderLeft: `3px solid ${rule}` } : {}), ...style }}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: hint ? 2 : hasBody ? 12 : 0 }}>
          {title && <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: tone === 'gate' ? 'var(--gate)' : 'var(--muted)', margin: 0, lineHeight: 1.4 }}>{title}</h3>}
          {action && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{action}</div>}
        </div>
      )}
      {hint && <p style={{ fontSize: '0.86rem', color: 'var(--body-ink)', lineHeight: 1.5, margin: hasBody ? '6px 0 14px' : '6px 0 0' }}>{hint}</p>}
      {children}
    </Card>
  );
}

// ── Note ───────────────────────────────────────────────────────────────────
// Quiet explanatory text (what this screen is for) — lower weight than an Alert banner.
export function Note({ children, style }) {
  return (
    <div style={{ fontSize: '0.84rem', color: 'var(--muted)', lineHeight: 1.55, padding: '0 6px', marginBottom: 14, ...style }}>
      {children}
    </div>
  );
}

// ── Stat ───────────────────────────────────────────────────────────────────
// A key number with its label (KPI tile). `quiet` drops the card frame.
export function Stat({ value, label, sub, quiet }) {
  const body = (
    <>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, color: 'var(--ink)' }}>{value}</div>
      <div style={{ fontSize: '0.8rem', color: 'var(--body-ink)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', marginTop: 2 }}>{sub}</div>}
    </>
  );
  return quiet
    ? <div style={{ minWidth: 0 }}>{body}</div>
    : <Card style={{ padding: '16px 20px', minWidth: 0 }}>{body}</Card>;
}

// Row of Stats that wraps on narrow screens.
export function StatRow({ children, min = 150, style }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 10, marginBottom: 14, ...style }}>{children}</div>;
}

// ── EmptyState ─────────────────────────────────────────────────────────────
export function EmptyState({ icon = '📋', title, message, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ink-soft)' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>{icon}</div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--ink)', marginBottom: '8px' }}>{title}</h3>
      {message && <p style={{ fontSize: '0.88rem', maxWidth: '320px', margin: '0 auto 20px' }}>{message}</p>}
      {action}
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────
export function Table({ headers, rows, emptyMessage }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: '8px 10px', textAlign: 'left',
                fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.08em',
                textTransform: 'uppercase', color: 'var(--muted)',
                borderBottom: '2px solid var(--ink-mid)', whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-faint)' }}>
                {emptyMessage || 'No data'}
              </td>
            </tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '12px 10px', color: 'var(--ink)', verticalAlign: 'top' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
